import { useState, useEffect } from "react";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_OAUTH_CLIENT_ID;
const GOOGLE_REDIRECT_URI = import.meta.env.VITE_GOOGLE_REDIRECT_URI;

export function useYouTubeAuth() {
  const [googleToken, setGoogleToken] = useState(null);
  const [googleUserInfo, setGoogleUserInfo] = useState(null);
  const [youtubePlaylists, setYouTubePlaylists] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("googleToken");
    if (token) setGoogleToken(token);
  }, []);

  const fetchUserInfoYoutube = async () => {
    if (!googleToken) {
      setGoogleUserInfo(null);
      return;
    }
    try {
      const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
        headers: { Authorization: `Bearer ${googleToken}` },
      });
      if (!res.ok) throw new Error("Failed to fetch user info");
      const data = await res.json();
      setGoogleUserInfo(data);
    } catch (e) {
      setGoogleUserInfo(null);
    }
  };

  useEffect(() => {
    fetchUserInfoYoutube();
  }, [googleToken]);

  useEffect(() => {
    const fetchPlaylists = async () => {
      if (!googleToken) {
        setYouTubePlaylists([]);
        return;
      }
      try {
        let playlists = [];
        let nextPageToken = '';
        do {
          const url = new URL('https://www.googleapis.com/youtube/v3/playlists');
          url.searchParams.set('part', 'snippet,contentDetails');
          url.searchParams.set('mine', 'true');
          url.searchParams.set('maxResults', '50');
          if (nextPageToken) url.searchParams.set('pageToken', nextPageToken);
          const res = await fetch(url.toString(), {
            headers: { Authorization: `Bearer ${googleToken}` },
          });
          if (!res.ok) throw new Error('Failed to fetch playlists');
          const data = await res.json();
          playlists = playlists.concat(data.items || []);
          nextPageToken = data.nextPageToken;
        } while (nextPageToken);
        setYouTubePlaylists(playlists);
      } catch (e) {
        setYouTubePlaylists([]);
      }
    };
    fetchPlaylists();
  }, [googleToken]);

  const youtubeLogin = () => {
    const scope = encodeURIComponent("openid email profile https://www.googleapis.com/auth/youtube https://www.googleapis.com/auth/youtube.force-ssl");
    const redirectUri = encodeURIComponent(GOOGLE_REDIRECT_URI);
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}&include_granted_scopes=true&prompt=consent`;

    const width = 500, height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      authUrl,
      "google_oauth",
      `width=${width},height=${height},left=${left},top=${top}`
    );

    const interval = setInterval(() => {
      try {
        if (!popup || popup.closed) {
          clearInterval(interval);
          return;
        }
        const hash = popup.location.hash;
        if (hash.includes("access_token")) {
          const params = new URLSearchParams(hash.substring(1));
          const token = params.get("access_token");
          if (token) {
            localStorage.setItem("googleToken", token);
            setGoogleToken(token);
            popup.close();
            clearInterval(interval);
          }
        }
      } catch {}
    }, 500);
  };

  const youtubeLogout = () => {
    setGoogleToken(null);
    setGoogleUserInfo(null);
    setYouTubePlaylists([]);
    localStorage.removeItem("googleToken");
  };

  return { googleToken, googleUserInfo, youtubePlaylists, youtubeLogin, youtubeLogout, fetchUserInfoYoutube };
}
